import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  input,
  OnDestroy,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChatComposerSendOverrideDirective } from './directives/chat-composer-send-override.directive';

export type ChatSourceId =
  | 'facebook'
  | 'google'
  | 'google-chat'
  | 'sms'
  | 'webchat'
  | 'openai'
  | 'platform'
  | 'email'
  | 'personal-email'
  | 'instagram'
  | 'whatsapp'
  | 'ai-assistant'
  | 'system';

export type AIModeId = 'fast' | 'thinking';

export type SourceId = ChatSourceId | AIModeId;

export interface SourceOption {
  id: string;
  sourceName: string;
  shortSourceName?: string;
  account?: string;
  icon?: string;
  materialIcon?: string;
  needsTranslation?: boolean;
}

export type ChatSource = Omit<SourceOption, 'id'> & { id: SourceId };

export interface SentMessage {
  messageText: string;
  sourceId?: SourceId;
  sourceName?: string;
  aiMode?: AIModeId;
}

@Component({
  selector: 'glxy-chat-composer',
  templateUrl: './chat-composer.component.html',
  styleUrls: ['./chat-composer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatComposerComponent implements OnDestroy, AfterViewInit {
  @HostBinding('class') class = 'glxy-chat-composer';

  @HostBinding('class.hide-extended-actions')
  get limitedActions(): boolean {
    // controls the hiding or showing of the 'extended-actions' element.
    // It is hidden when the chat composer component's width becomes too small
    return !this.showExtendedActions;
  }

  /**
   * Optional. The available chat sources to be shown in the chat sources
   * menu. If there is only one, that will be auto selected. If none, no
   * chat source text or selector is shown.
   */
  chatSources = input<ChatSource[]>([]);

  /**
   * The messages textarea's content. You can use this input
   * to read or modify the text, such as adding links and content
   * to it.
   */
  @Input() public messageText = '';

  /**
   * Optional. The placeholder text for the message textarea.
   * Defaults to a translated "Type here…" when not provided.
   */
  @Input() public placeholder?: string;

  /**
   * Optional. The text in the Send button.
   * Defaults to a translated "Send" when not provided.
   */
  @Input() public sendText?: string;

  /**
   * Optional. Set the selected chat source from those available in the
   * `chatSources` input above. If none set, the component will default to
   * the first chatSource item
   */
  selectedChatSourceId = input<SourceId>();

  /**
   * Whether to send the message if you hit the `enter` key on your keyboard
   */
  @Input({ transform: booleanAttribute }) enterKeySendsMessage = true;

  /* Optional. Controls the min height of the text area in the composer */
  @Input() textAreaMinRows? = 1;

  /* Optional. Controls the max height of the text area in the composer */
  @Input() textAreaMaxRows? = 5;

  /* Optional. Will disable send button and show a loading spinner if processing */
  @Input({ transform: booleanAttribute }) processing = false;

  /* Optional. Will disable the text box and send button if disabled is true */
  @Input({ transform: booleanAttribute }) disabled = false;

  /* Optional. Will clear the message text area if set to false */
  @Input({ transform: booleanAttribute }) clearMessageTextOnSend = true;

  /* Optional. Will not send out a message when message text is empty if disabled is true */
  @Input({ transform: booleanAttribute }) disableSendEmptyText = true;

  /* Optional. Will set the max length of the text area if provided */
  @Input() maxlength?: number;

  /* Optional. Specifies whether the send button will use a custom color class or fallback to the galaxy theme*/
  @Input({ transform: booleanAttribute }) customColor = false;

  /* Optional. Will disable editing of the textbox content if set to true */
  @Input({ transform: booleanAttribute }) readonly = false;

  /**
   * Optional. Set to cover the text area with this label and an animated indicator, for work that
   * fills the composer rather than sending from it — generating a reply, say. Does not stop typing
   * on its own: pair it with `readonly` so the text underneath cannot be edited while it runs.
   */
  public readonly loadingText = input<string>();

  /**
   * Emitted when the user sends a message.
   */
  @Output() send: EventEmitter<SentMessage> = new EventEmitter();

  /**
   * Emitted whenever the composer changes its own text — typing, clearing on send,
   * inserting dynamic content — so `[(messageText)]` keeps a parent in step with what
   * the user actually sees. Not emitted for text pushed in through the input.
   */
  @Output() messageTextChange: EventEmitter<string> = new EventEmitter();

  /**
   * Emitted when the user changes chat sources.
   */
  @Output() selectedChatSourceIdChange: EventEmitter<SourceId> = new EventEmitter();

  @ViewChild('composerTextArea', { static: true })
  public composerTextArea!: ElementRef<HTMLTextAreaElement>;

  private readonly projectedSendOverride = contentChild(ChatComposerSendOverrideDirective);

  protected readonly hasSendOverride = computed(() => !!this.projectedSendOverride());

  currentSourceId?: SourceId;
  currentSource?: ChatSource;

  showDynamicContentMinWidthPx = 400;
  showFullSourceNameMinWidthPx = 500;

  firstSource = computed(() => {
    return this.chatSources()?.[0];
  });

  // element resizing for mobile and small screens
  showExtendedActions = false;
  showFullSourceName = false;

  private resizeObserver!: ResizeObserver;
  private showDynamicContentStore = false;
  private showSourceNameStore = false;

  get currentSourceDisplayName(): string {
    return this.currentSource?.shortSourceName || this.currentSource?.sourceName || '';
  }

  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = Array.isArray(entries) ? entries[0] : entries;
        this.toggleDynamicContent(entry.contentRect.width);
        this.toggleSourceName(entry.contentRect.width);
      });
      this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    effect(() => {
      const firstChatSource = this.firstSource();
      // if no current chat source set, then use first in array
      if (!this.currentSourceId && firstChatSource) {
        this.changeChatSourceId(firstChatSource.id);
      } else if (this.currentSourceId) {
        this.changeChatSourceId(this.currentSourceId);
      }
    });

    effect(() => {
      this.changeChatSourceId(this.selectedChatSourceId());
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  sendMessage(event?: Event): void {
    // dont send if processing
    if (this.processing) {
      return;
    }

    // when hitting the enter key, we must stop the textinput
    // from adding a new line
    if (event) {
      event.preventDefault();
    }

    // focus the editor after sending to keep mobile
    // keyboard visible.
    this.focusTextArea();

    const messageText = this.messageText?.trim() ?? '';

    // dont sent if meessage is blank
    if (this.disableSendEmptyText && !messageText) return;

    const messageToSend: SentMessage = {
      messageText,
      sourceId: this.currentSourceId,
      sourceName: this.currentSource?.sourceName,
    };
    this.send.emit(messageToSend);

    if (this.clearMessageTextOnSend) {
      // clear message after sending
      this.clearMessageText();
    }
  }

  chatSourceLabel(): string {
    const shortSourceName = this.currentSource?.shortSourceName?.trim();
    const sourceName = this.currentSource?.sourceName?.trim();
    const account = this.currentSource?.account?.trim();
    const resolvedSourceName = this.showFullSourceName ? sourceName : shortSourceName || sourceName;

    if (!resolvedSourceName && !account) {
      return '';
    }

    const labelParts = [];
    if (resolvedSourceName) {
      labelParts.push(
        this.needsTranslation(this.currentSource)
          ? this.translateService.instant(resolvedSourceName)
          : resolvedSourceName,
      );
    }

    if (account) {
      labelParts.push(this.needsTranslation(this.currentSource) ? this.translateService.instant(account) : account);
    }

    const label = labelParts.join(' ').trim();
    return label
      ? this.translateService.instant('GALAXY.CHAT_COMPOSER.CHAT_SOURCE_LABEL_PREFIX', { sourceName: label })
      : '';
  }

  public setMessageText(text: string): void {
    this.messageText = text;
    this.messageTextChange.emit(text);
  }

  public clearMessageText(): void {
    this.setMessageText('');
    if (this.composerTextArea?.nativeElement) {
      this.composerTextArea.nativeElement.value = '';
    }
    this.resizeTextArea();
  }

  public focusTextArea(options?: FocusOptions): void {
    this.composerTextArea.nativeElement.focus(options);
  }

  changeChatSourceId(sourceId: SourceId | undefined, emitChanges = false): void {
    if (this.chatSources()?.length === 0 || !sourceId) return;

    const newSource = this.chatSources()?.find((obj) => {
      return obj.id === sourceId;
    });

    if (newSource) {
      this.currentSourceId = sourceId;
      this.currentSource = newSource;
    } else {
      // if no source is found with the id then use the first
      // source in the list
      this.currentSourceId = this.chatSources()?.[0].id;
      this.currentSource = this.chatSources()?.[0];
    }

    if (emitChanges) {
      this.selectedChatSourceIdChange.emit(sourceId);
    }
  }

  public insertDynamicContent(
    dynamicContent: string,
    position: 'before' | 'after' | 'curser' = 'curser',
    selectInsertedCurserText = false,
  ): void {
    const textArea = this.composerTextArea.nativeElement;

    // If there is no message text, then don't add new lines to output
    const spacing = this.messageText !== '' ? '\n\n' : '';

    const startPos = textArea.selectionStart;
    const endPos = textArea.selectionEnd ?? startPos;

    let newValue: string;

    if (position === 'before') {
      dynamicContent = dynamicContent + spacing;
      newValue = dynamicContent + this.messageText;
    } else if (position === 'after') {
      dynamicContent = spacing + dynamicContent;
      newValue = this.messageText + dynamicContent;
    } else {
      // position is `curser`
      const contentBefore = textArea.value.substr(0, startPos);
      const contentAfter = textArea.value.substr(endPos, textArea.value.length);

      // add space before and after dynamic content if needed
      if (contentBefore && !contentBefore.endsWith(' ')) {
        dynamicContent = ' ' + dynamicContent;
      }
      if (!contentAfter.startsWith(' ')) {
        dynamicContent = dynamicContent + ' ';
      }
      newValue = contentBefore + dynamicContent + contentAfter;
    }

    this.setMessageText(newValue);
    textArea.value = newValue;

    // Using window.setTimeout fix bug where input does not focus
    window.setTimeout(() => {
      if (position === 'curser') {
        if (selectInsertedCurserText) {
          textArea.setSelectionRange(startPos, dynamicContent.length + startPos);
        } else {
          const curserPosition = dynamicContent.length + startPos;
          textArea.setSelectionRange(curserPosition, curserPosition);
        }
      } else if (position === 'after') {
        textArea.setSelectionRange(textArea.value.length, textArea.value.length);
      } else if (position === 'before') {
        textArea.setSelectionRange(0, 0);
      }
      textArea.focus();
      // manually trigger change detection to fix bug where it doesn't trigger properly after inserting text
      this.changeDetectorRef.detectChanges();
      this.resizeTextArea();
    }, 0);
  }

  // Hide and show content based on the width of the component

  toggleDynamicContent(width: number): void {
    const updatedValue = width > this.showDynamicContentMinWidthPx;

    if (updatedValue === this.showDynamicContentStore) return;

    this.showDynamicContentStore = updatedValue;
    // window.setTimeout to fix detectChanges not firing
    window.setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
    this.showExtendedActions = updatedValue;
  }

  toggleSourceName(width: number): void {
    const updatedValue = width > this.showFullSourceNameMinWidthPx;
    if (updatedValue === this.showSourceNameStore) return;

    this.showSourceNameStore = updatedValue;
    // window.setTimeout to fix detectChanges not firing
    window.setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
    this.showFullSourceName = updatedValue;
  }

  ngAfterViewInit() {
    this.resizeTextArea();
  }

  resizeTextArea(): void {
    const textarea = this.composerTextArea.nativeElement;
    if (!textarea) return;

    // Reset height to auto to get accurate scrollHeight measurement
    textarea.style.height = 'auto';

    const singleLineHeight = parseFloat(getComputedStyle(textarea).lineHeight);
    const minHeight = (this.textAreaMinRows ?? 1) * singleLineHeight;
    const maxHeight = (this.textAreaMaxRows ?? 5) * singleLineHeight;

    // Always use scrollHeight for consistent calculation, but ensure minimum height
    const calculatedHeight = Math.max(minHeight, Math.min(maxHeight, textarea.scrollHeight));

    // Only show scrollbar if content exceeds max height
    const shouldShowScrollbar = textarea.scrollHeight > maxHeight;
    textarea.style.overflowY = shouldShowScrollbar ? 'auto' : 'hidden';

    requestAnimationFrame(() => (textarea.style.height = `${calculatedHeight}px`));
  }

  needsTranslation(source?: ChatSource): boolean {
    return source?.needsTranslation ?? false;
  }
}
